import { useState, useEffect, useRef, useCallback } from 'react';
import ReactPlayer from 'react-player';
import { Box, Typography } from '@mui/material';
import Iconify from '../../../../components/iconify';
import { getStreamingLink } from '../../../../services/dataCaptureService';
import { useTimeline } from '../TimelineContext';
import { globalToLocalTime } from '../timelineSync';

const ROW_HEIGHT = 80; // must match WorkspaceGrid rowHeight

export default function VideoWidget({ recording, widgetConfig, onUpdateWidget }) {
  const { currentTime, isPlaying, seek } = useTimeline();
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(true);
  const playerRef = useRef(null);
  const seekingRef = useRef(false);
  const hasResized = useRef(false);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);
  const containerRef = useRef(null);

  const fetchUrl = useCallback(async () => {
    try {
      const link = await getStreamingLink(recording.id);
      setUrl(link);
      setError(false);
      retryCountRef.current = 0;
    } catch (err) {
      if (retryCountRef.current < 3) {
        retryCountRef.current += 1;
        retryTimerRef.current = setTimeout(fetchUrl, 1000 * retryCountRef.current);
      } else {
        setError(true);
      }
    }
  }, [recording.id]);

  useEffect(() => {
    fetchUrl();
    return () => { if (retryTimerRef.current) clearTimeout(retryTimerRef.current); };
  }, [fetchUrl]);

  // Re-fetch URL when tab becomes visible again (display changes from none to block)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && error) {
        retryCountRef.current = 0;
        setError(false);
        fetchUrl();
      }
    }, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [error, fetchUrl]);

  // Seek player when timeline position changes while paused
  useEffect(() => {
    if (isPlaying || seekingRef.current || !playerRef.current) return;
    const localTime = globalToLocalTime(currentTime, recording);
    if (localTime !== null) {
      playerRef.current.seekTo(localTime / 1000, 'seconds');
    }
  }, [currentTime, isPlaying, recording]);

  const handlePlayerError = () => {
    if (retryCountRef.current < 3) {
      retryCountRef.current += 1;
      retryTimerRef.current = setTimeout(fetchUrl, 1000 * retryCountRef.current);
    } else {
      setError(true);
    }
  };

  const handleProgress = ({ playedSeconds }) => {
    if (!isPlaying) return;
    const globalTime = recording.startTime + playedSeconds * 1000;
    seekingRef.current = true;
    seek(globalTime);
    seekingRef.current = false;
  };

  const handleReady = () => {
    if (hasResized.current || !playerRef.current || !onUpdateWidget) return;
    const internal = playerRef.current.getInternalPlayer();
    if (!internal) return;

    const videoWidth = internal.videoWidth || internal.clientWidth;
    const videoHeight = internal.videoHeight || internal.clientHeight;
    if (!videoWidth || !videoHeight) return;

    hasResized.current = true;
    const aspect = videoWidth / videoHeight;
    const currentW = widgetConfig?.position?.w || 4;
    // Calculate grid h from aspect ratio: pixelWidth = currentW * (containerWidth/12), pixelHeight = pixelWidth / aspect
    // Approximate: each grid column ~100px, so pixelWidth ≈ currentW * 100
    const pixelWidth = currentW * 100;
    const pixelHeight = pixelWidth / aspect;
    const newH = Math.max(2, Math.round(pixelHeight / ROW_HEIGHT));

    if (widgetConfig?.position?.h !== newH) {
      onUpdateWidget({ position: { ...widgetConfig.position, h: newH } });
    }
  };

  if (!active) {
    return (
      <Box
        ref={containerRef}
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', cursor: 'pointer', bgcolor: 'grey.100' }}
        onClick={() => setActive(true)}
      >
        <Iconify icon="eva:play-circle-outline" width={48} sx={{ color: 'text.secondary', mb: 1 }} />
        <Typography variant="caption" color="text.secondary">Click to activate</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box ref={containerRef} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography color="error" variant="body2">Failed to load video</Typography>
      </Box>
    );
  }

  if (!url) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography color="text.secondary" variant="body2">Loading video...</Typography>
      </Box>
    );
  }

  const localTime = globalToLocalTime(currentTime, recording);
  const isInRange = localTime !== null;

  return (
    <Box ref={containerRef} sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <ReactPlayer
        ref={playerRef}
        url={url}
        playing={isPlaying && isInRange}
        width="100%"
        height="100%"
        onError={handlePlayerError}
        onProgress={handleProgress}
        onReady={handleReady}
        progressInterval={250}
        controls={false}
      />
    </Box>
  );
}
